<template>
  <div class="AdvantageSliderComponent">
    <PlusMinusComponent :id="`${props.id}-plus-minus`" @decrease="decreaseValue" @increase="increaseValue"
      :value="formattedValue" :decreaseDisabled="decreaseDisabled" :increaseDisabled="increaseDisabled" />

    <div class="slider-content">
      <label :for="`${props.id}-slider`">{{ footerMessage }}</label>
      <v-slider :id="`${props.id}-slider`" class="slider" :model-value="sliderValue"
        :step="props.type === 'stepper' ? step : 1" :min="props.type === 'stepper' ? min : 0"
        :max="props.type === 'stepper' ? max : (props.indexedValues?.length ?? 1) - 1" thumb-color="#fff"
        track-color="#ddd" track-size="8" color="var(--vt-c-primary)" :messages="footerMessage"
        :hide-details="!footerMessage" @update:modelValue="updateValueBySlider" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { CURRENCY } from '@/modules/common/common/constants/CommonConstants'
import PlusMinusComponent from '@/modules/core/components/utils/PlusMinusComponent.vue'
import { addCommas } from '@/utils/textUtils'
import { computed, ref, watch } from 'vue'

interface PropsInterface {
  id: string
  modelValue?: number | string
  value?: number | string
  min: number | string
  max: number | string
  step?: number
  units?: string
  period?: string
  //* slider max limit (lower than max value)
  limit?: number | null
  //* slider footer message (example => possible values)
  footerMessage?: string
  type?: 'stepper' | 'indexed'
  defaultIndex?: number
  indexedValues?: number[]
}
const props = withDefaults(defineProps<PropsInterface>(), {
  modelValue: undefined,
  value: 0,
  min: 0,
  max: 10,
  step: 1,
  units: CURRENCY,
  period: '',
  limit: null,
  footerMessage: '',
  type: 'stepper',
  defaultIndex: 0,
  indexedValues: () => []
})

const initValue = props.modelValue ?? props.value
const innerValue = ref(
  initValue >= props.min ? (initValue > props.max ? props.max : initValue) : props.min
)
const sliderValue = ref(props.type === 'stepper' ? Number(innerValue.value) : props.defaultIndex)
const decreaseDisabled = ref(
  props.type === 'stepper' ? sliderValue.value === props.min : sliderValue.value === 0
)
const increaseDisabled = ref(
  props.type === 'stepper'
    ? innerValue.value === (props.limit ?? props.max)
    : sliderValue.value === props.indexedValues.length - 1
)

const formattedValue = computed(
  () => `${addCommas(innerValue.value)} ${props.units}${props.period}`
)

const emit = defineEmits(['update:modelValue'])

watch(
  () => props.limit,
  (currentLimit) => validateLimit(currentLimit),
  { immediate: true }
)

watch(
  () => [props.min, props.max, props.limit],
  ([min, max, limit]) => {
    if (props.type === 'stepper') {
      const value = Math.max(Number(min), Number(innerValue.value))
      innerValue.value = Math.min(value, Number(limit ?? max))
      sliderValue.value = innerValue.value
    }
  }
)

watch(
  () => props.modelValue,
  (value) => {
    if (value) {
      innerValue.value = Number(value)
      sliderValue.value = innerValue.value
    }
  }
)

watch(
  () => innerValue.value,
  (currentValue) => {
    if (props.type === 'stepper') {
      innerValue.value = getValueWithinRange(Number(currentValue))
    } else {
      innerValue.value = Number(currentValue)
    }

    emit('update:modelValue', innerValue.value)
  },
  { immediate: true }
)

function validateLimit(limitValue: number | null): void {
  if (limitValue === null) return

  if (limitValue > Number(props.max)) {
    throw new Error('AdvantageSliderComponent: props.limit must be lower than props.max')
  }
}

function decreaseValue(): void {
  if (props.type === 'indexed') {
    if (sliderValue.value > 0) sliderValue.value -= 1
    innerValue.value = Number(props.indexedValues[sliderValue.value])
    decreaseDisabled.value = sliderValue.value === 0
  } else {
    const expectedNewValue = Number(innerValue.value) - props.step
    innerValue.value = getValueWithinRange(expectedNewValue)
    sliderValue.value = innerValue.value
    decreaseDisabled.value = sliderValue.value === props.min
  }
  increaseDisabled.value = false
}

function increaseValue(): void {
  if (props.type === 'indexed') {
    if (sliderValue.value < props.indexedValues.length - 1) sliderValue.value += 1
    innerValue.value = Number(props.indexedValues[sliderValue.value])
    increaseDisabled.value = sliderValue.value === props.indexedValues.length - 1
  } else {
    const expectedNewValue = Number(innerValue.value) + props.step
    innerValue.value = getValueWithinRange(expectedNewValue)
    sliderValue.value = innerValue.value
    increaseDisabled.value = innerValue.value === (props.limit ?? props.max)
  }
  decreaseDisabled.value = false
}

function getValueWithinRange(value: number): number {
  const limit = props.limit ?? props.max
  const maxValue = Math.min(Number(limit), value)

  return Math.max(Number(props.min), maxValue)
}

function updateValueBySlider(value: number) {
  if (props.type === 'indexed') {
    innerValue.value = Number(props.indexedValues[value])
    sliderValue.value = value
    increaseDisabled.value = sliderValue.value === props.indexedValues.length - 1
    decreaseDisabled.value = sliderValue.value === 0
  } else {
    innerValue.value = getValueWithinRange(value)
    sliderValue.value = innerValue.value
    increaseDisabled.value = innerValue.value === (props.limit ?? props.max)
    decreaseDisabled.value = sliderValue.value === props.min
  }
}
</script>

<style lang="scss" scoped>
.AdvantageSliderComponent {
  max-width: $size-600;
  margin: auto;

  .slider-content {
    :deep(.v-input__details) {
      color: $color-neutral;

      .v-messages {
        opacity: 1;
        font-size: $font-size-s;
      }
    }
  }

  .slider :deep(.v-slider-thumb__surface) {
    box-shadow: inset 0 0 0 3px var(--vt-c-primary) !important;
    border-radius: $b-radius-50;
  }

  label {
    visibility: hidden;
    display: flex;
    max-width: 0;
    max-height: 0;
  }
}
</style>
